import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { useOverlay } from '@fs/zion-ui'
import { trackEvent } from '@fs/zion-analytics'
import { ChatContext } from './useChatProviderContext'
import { isLinkApproved } from '../helpers/domainWhiteList'
import useFetchWhitelist from '../hooks/useFetchWhitelist'

const ChatOverlay = lazy(() => import('../chat-overlay/ChatOverlay'))
const ChatProfileInterrupter = lazy(() => import('../profile-interrupter/ChatProfileInterrupter'))

ChatOverlay.displayName = 'ChatOverlay'
ChatProfileInterrupter.displayName = 'ChatProfileInterrupter'

export function useChatProviderData(mockChatProviderData) {
  const [focusBackRef, setFocusBackRef] = useState(undefined)
  const overlay = useOverlay({ focusBackRef })
  const [chatRoute, setChatRoute] = useState()
  const [supportInformation, setSupportInformation] = useState()
  const [forceBypassStartupDialogs, setForceBypassStartupDialogs] = useState(false)
  const [domainWhitelist, setDomainWhitelist] = useState([])
  const chatProfileInterrupterOverlay = useOverlay()
  const clientConsumerCountRef = useRef(0)
  useFetchWhitelist({ setDomainWhitelist, isOpen: overlay.isOpen, isTransitioning: overlay.transitioning })

  if (mockChatProviderData) {
    return mockChatProviderData
  }

  return {
    overlay,
    chatRoute,
    setChatRoute,
    supportInformation,
    setSupportInformation,
    chatProfileInterrupterOverlay,
    clientConsumerCountRef,
    forceBypassStartupDialogs,
    setForceBypassStartupDialogs,
    updateFocusBackRef: setFocusBackRef,
    domainWhitelist,
  }
}

export default function ChatProvider({ children, mockChatProviderData }) {
  const chatProviderData = useChatProviderData(mockChatProviderData)
  const {
    overlay,
    supportInformation,
    setSupportInformation,
    chatProfileInterrupterOverlay,
    setChatRoute,
    domainWhitelist,
  } = chatProviderData

  // Open chat when interrupter starts to close (interrupter is conditionally rendered, so its onClose won’t fire)
  const wasOpenedRef = useRef(false)
  const interrupterOpen = chatProfileInterrupterOverlay.isOpen
  const openChat = overlay.open
  useEffect(() => {
    if (interrupterOpen) {
      wasOpenedRef.current = true
    } else if (wasOpenedRef.current) {
      openChat()
    }
  }, [interrupterOpen, openChat])

  useEffect(() => {
    const handleUrlParams = () => {
      if (typeof window === 'undefined') return
      const searchParams = new URLSearchParams(window.location.search)
      if (!searchParams.has('chatRoute')) return
      const chatRouteParam = searchParams.get('chatRoute')
      setChatRoute(chatRouteParam)
      const aboutTextParam = searchParams.get('aboutText')
      const aboutLinkParam = searchParams.get('aboutLink')
      const messageTextParam = searchParams.get('messageText')
      // Filter out unapproved links to prevent abuse
      if (aboutLinkParam && isLinkApproved(aboutLinkParam, domainWhitelist)) {
        setSupportInformation((prev) => {
          return { ...prev, about: { text: aboutTextParam ?? aboutLinkParam, link: aboutLinkParam } }
        })
      }
      if (messageTextParam) {
        setSupportInformation((prev) => {
          return { ...prev, messageText: messageTextParam }
        })
      }
      trackEvent({
        event_name: 'chat_route_from_url',
        link_name: 'Chat:Open from URL',
        chatRoute: chatRouteParam,
        about: { aboutTextParam, aboutLinkParam },
        messageText: messageTextParam,
        url: window.location.href,
      })
      openChat()
    }
    handleUrlParams()
  }, [openChat, setChatRoute, setSupportInformation, domainWhitelist])

  return (
    <ChatContext.Provider value={chatProviderData}>
      {children}
      <Suspense fallback={null}>
        {(chatProfileInterrupterOverlay.isOpen || chatProfileInterrupterOverlay.transitioning) && (
          <ChatProfileInterrupter overlay={chatProfileInterrupterOverlay} />
        )}
        {(overlay.isOpen || overlay.transitioning) && (
          <ChatOverlay supportInformation={supportInformation} overlay={overlay} />
        )}
      </Suspense>
    </ChatContext.Provider>
  )
}
